import { Dispatch } from 'react';
import track, { errorTypes } from 'c9-tracking';
import { IConfigProps } from '../context/ConfigContext';
import {
    RESET_PACKAGE,
    SET_DATA_MODEL_VALUE,
    SET_DID_CLICK_FETCH_PACKAGES,
    SET_ERROR,
    SET_PURCHASE_COMPLETE,
    SET_SELECTION_VALUE
} from '../store/ActionKeys';
import { Endpoint, genericRequest, setSelectionValue } from '../store/Actions';
import { apiGatewayBase, wso2header } from '../constants';
import {
    BLOCK_TYPES,
    Deductible,
    Generic,
    IAction,
    Model,
    OnloadFn,
    OnloadFnPackagePage,
    OnloadFnFinalizePurchasePage,
    Package,
    PackageInfo,
    Packages,
    PContext,
    ProductNames,
    Selection,
    Stakeholder,
    TError,
    PackageResponse,
    VinCheck,
    WhitelabelPartner
} from '../interfaces/interfaces';
import {
    EMAIL_IDENTIFIER,
    IEmailPayload,
    RMOEmailPayloadCar,
    RMOEmailPayloadChild,
    RMOEmailPayloadCommon,
    SendRmoEMail
} from '../interfaces/interfaces-email';
import dateFormatter, { cpr2BirthDate } from './formatter-date';
import { IUserContextProps } from '../context/UserContext';

export const setError = ({
    dispatch,
    errorMessage,
    redirectToPage,
    hasError,
    manufacturerDataDeficient,
    operation
}: TError) => {
    dispatch({
        type: SET_ERROR,
        payload: {
            errorMessage,
            redirectToPage,
            hasError,
            manufacturerDataDeficient,
            operation
        }
    });
};

export const resetError = (dispatch: Dispatch<IAction>) => {
    dispatch({
        type: SET_ERROR,
        payload: {
            errorMessage: undefined,
            redirectToPage: undefined,
            hasError: false,
            manufacturerDataDeficient: false
        }
    });
};

// Returns the number of years from a specific date
export const ageInYears = (dateStr: string) => {
    const today = Date.now();
    const date = new Date(dateStr).getTime();
    const age = new Date(today - date).getFullYear() - 1970;
    return age < 0 ? 0 : age;
};

type Cache = {
    selection?: Selection;
    model?: Model;
    fieldValidation?: Record<string, boolean | undefined>;
};
export const saveToCache = ({ selection, model }: Cache) => {
    if (selection) {
        sessionStorage.setItem('selection', JSON.stringify(selection));
    }

    if (model) {
        sessionStorage.setItem('model', JSON.stringify(model));
    }
};

export const deleteCache = () => {
    sessionStorage.removeItem('selection');
    sessionStorage.removeItem('model');
};

// uppercase first letter and lowercase the rest
export const uppercaseFirst = (text: string): string => {
    text = text.toLowerCase();
    return text.charAt(0).toUpperCase() + text.slice(1);
};

export const resetPackage = ({ config, dispatch }: Pick<OnloadFn, 'config' | 'dispatch'>) => {
    const modelCache = sessionStorage.getItem('model');
    const cachedModel = !!modelCache && JSON.parse(sessionStorage.getItem('model') ?? '');
    if (cachedModel) {
        sessionStorage.setItem(
            'model',
            JSON.stringify({
                ...cachedModel,
                [config.product]: {
                    ...cachedModel[config.product],
                    packages: null
                }
            })
        );
    }

    dispatch({
        type: RESET_PACKAGE,
        payload: {
            productKey: config.product
        }
    });
};

export const setDidClickFetchPackages = ({
    dispatch,
    didClick
}: {
    dispatch: Dispatch<IAction>;
    didClick: boolean;
}) => {
    dispatch({
        type: SET_DID_CLICK_FETCH_PACKAGES,
        payload: didClick
    });
};

export const login = (redirectPath: string, config: IConfigProps) => {
    const originalHost = window.topContext.config.getSiteConfig().originalHost;
    const targetUrl = `${window.location.protocol}//${originalHost}${config.appBasePath}${redirectPath}`;
    window.topContext.login.startPopup({
        appName: `ONSA-${config.product}`,
        targetUrl: targetUrl
    });
};

export const productName = (productKey: string) => {
    const names: Generic<string> = {
        CAR: 'Bilforsikring',
        CHILD: 'Børneforsikring'
    };
    return names[productKey];
};

export const getCustomerMarking = async ({ config, dispatch }: Pick<OnloadFn, 'config' | 'dispatch'>) => {
    const endpoint = {
        ...config.endpoint.customerMarking,
        url: `${config.endpoint.customerMarking.url}?product=${config.product}`
    };

    const response = await genericRequest(endpoint);
    dispatch({
        type: SET_DATA_MODEL_VALUE,
        payload: {
            productKey: config.product,
            dataKey: 'customerMarking',
            value: response
        }
    });
};

export const getStakeholder = async ({ config, dispatch }: Pick<OnloadFn, 'config' | 'dispatch'>) => {
    const response = await genericRequest(config.endpoint.stakeholder);
    if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i.test(response.emailAddress)) {
        response.emailAddress = '';
    }
    dispatch({
        type: SET_DATA_MODEL_VALUE,
        payload: {
            productKey: config.product,
            dataKey: 'stakeholder',
            value: response
        }
    });
    return response;
};

export const getEboksSubscription = async ({
    config,
    dispatch,
    customerId
}: {
    config: IConfigProps;
    dispatch: Dispatch<IAction>;
    customerId: string | undefined;
}) => {
    const cid = customerId?.split(',')[0];
    const response = await genericRequest({
        method: 'GET',
        url: `${apiGatewayBase}/v2/GTEboks/GTEboks/status?brand=TD&id=${cid}`,
        headers: wso2header
    });

    dispatch({
        type: SET_DATA_MODEL_VALUE,
        payload: {
            productKey: config.product,
            dataKey: 'eboks',
            value: response
        }
    });
};

export const getPbsSubscription = async ({
    config,
    dispatch,
    selectedPackage
}: {
    config: IConfigProps;
    dispatch: Dispatch<IAction>;
    selectedPackage: Package;
}) => {
    const { quoteId, productId } = selectedPackage;
    const response = await genericRequest({
        method: 'GET',
        url: `${apiGatewayBase}/v2/onlinesales-services/api/payments/pbs?quoteId=${quoteId}&productId=${productId}`,
        headers: wso2header
    });

    dispatch({
        type: SET_DATA_MODEL_VALUE,
        payload: {
            productKey: config.product,
            dataKey: 'pbs',
            value: response
        }
    });
};

export const handleContactProceed = async ({
    config,
    selection,
    model,
    user
}: Required<Omit<OnloadFn, 'dispatch'>>) => {
    // if user is logged in and wants to update contact data, call GTI
    if ((user as IUserContextProps).loggedIn && !!selection.updateContactData) {
        const endpoint = {
            ...config.endpoint.userprofile,
            url: `${config.endpoint.userprofile.url}`
        };
        const payload = {
            emailAddress: selection.emailAddress ?? model[config.product].stakeholder?.emailAddress,
            mobilePhone: selection.mobilePhone ?? model[config.product].stakeholder?.mobilePhone
        };
        await genericRequest(endpoint, payload);
    }
};

// check if vehicle registration costs are unpaid..
export const getDfimStatus = async (config: IConfigProps, dispatch: Dispatch<IAction>) => {
    const response = await genericRequest(config.endpoint.dStatus);

    dispatch({
        type: SET_DATA_MODEL_VALUE,
        payload: {
            productKey: config.product, //should only be called for car
            dataKey: 'dfimStatus',
            value: response.isValid
        }
    });
};

export const getPackages = async ({ config, dispatch, selection, model, user }: Required<OnloadFn>) => {
    // get packages
    const endpoint = (user as IUserContextProps).loggedIn
        ? config.endpoint.packagesGetPrivate
        : config.endpoint.packagesGetPublic;
    const quoteString = model[config.product].packages?.packages.map((p: Package) => p.quoteId).join('&quoteid=');

    const getResponse: PackageResponse = await genericRequest({
        ...endpoint,
        url: `${endpoint.url}?productkey=${config.product}&brand=TD&quoteid=${quoteString}`
    });

    // Merge new prices with existing package data
    mergeNewPrices({ config, dispatch, selection, model, data: getResponse });

    return getResponse;
};

// This function merges the GET/PUT packages data when updating deductible/mileage etc. with existing package data in the store (in "model" and "selection")
export const mergeNewPrices = ({
    config,
    dispatch,
    selection,
    model,
    data
}: Required<Omit<OnloadFn, 'user'>> & { data: PackageResponse }) => {
    // Merge new prices into model (the package data we already have)
    let originalPackagesObject = model[config.product].packages ?? ({} as Packages);
    const newPackagesObject = {
        ...originalPackagesObject,
        packages: originalPackagesObject.packages.map((p: Package) => {
            const updatedPriceObject = data.packages.find((up: Package) => up.quoteId === p.quoteId);
            return { ...p, ...updatedPriceObject };
        }),
        additionalCoverages: data.additionalCoverages ?? model[config.product].packages?.additionalCoverages
    };

    dispatch({
        type: SET_DATA_MODEL_VALUE,
        payload: {
            productKey: config.product,
            dataKey: 'packages',
            value: newPackagesObject
        }
    });

    const modelObject = { ...model, [config.product]: { ...model[config.product], packages: newPackagesObject } };
    saveToCache({ model: modelObject });

    // Merge new prices into selected package (if any)
    if (selection.package) {
        const updatedPriceObject = data.packages.find((up: Package) => up.quoteId === selection.package?.quoteId);
        const newSelectionObject = { ...selection.package, ...updatedPriceObject };
        dispatch({
            type: SET_SELECTION_VALUE,
            payload: {
                key: 'package',
                value: newSelectionObject
            }
        });
        saveToCache({ selection: { ...selection, package: newSelectionObject } });
    }
};

export const updateDisabilitySum = async ({
    config,
    selection,
    model
}: {
    config: IConfigProps;
    selection: Selection;
    model: Model;
}) => {
    const endpoint = config.endpoint.updateDisabilitySum;
    const payload = {
        brand: 'TD',
        disabilitySum: selection.disabilitySum,
        quoteIds: model[config.product].packages?.packages.map((p: Package) => p.quoteId),
        productName: config.product
    };

    return await genericRequest(endpoint, payload);
};

export const updatePackages = async (
    { config, dispatch, selection, model, user }: Required<OnloadFn>,
    packageInfo: {
        name: PackageInfo;
        value: boolean;
    } | null = null
) => {
    const endpoint =
        (user as IUserContextProps)?.loggedIn && selection.isCustomer
            ? config.endpoint.updatePackagesPrivate
            : config.endpoint.updatePackagesPublic;

    let payload = {
        brand: 'TD',
        quoteIds: model[config.product].packages?.packages.map((p: Package) => p.quoteId),
        additionalCoverages: selection.selectedAdditionalCoverages ?? [],
        cprNo: selection.cprNo,
        deductible:
            config.product === ProductNames.CHILD
                ? 0
                : (selection.deductible ??
                  model[config.product].packages?.deductibles.find((d: Deductible) => d.defaultDeductible)?.amount ??
                  null),
        productKey: config.product,
        packageInfo,
        leasingCarReturn: false,
        productInfo: {}
    } as any;
    if (config.product === ProductNames.CAR) {
        payload.leasingCarReturn = model['CAR'].showLeasingCarReturn;
        payload.productInfo = {
            leasingCar: !!selection.leasingCar || (!!model.CAR.dmr && !!model.CAR.dmr.isLeasingCar)
        };
    }
    const getResponse = await genericRequest(endpoint, payload);

    // Merge new prices with existing package data
    mergeNewPrices({ config, dispatch, selection, model, data: getResponse });
};

const fixDefaultPackageForChild = (packages: Package[], criticalIllnessCoverage: boolean) => {
    return packages.map((item) => {
        item.defaultSelected =
            (criticalIllnessCoverage && item.packageName.toUpperCase() === 'STANDARD') ||
            (!criticalIllnessCoverage && item.packageName.toUpperCase() === 'BASIS');
        return item;
    });
};

export const updateAdditionalCoverages = (packageData: any, selection: Selection, dispatch: Dispatch<IAction>) => {
    let selectedAdditionalCoverages = selection?.selectedAdditionalCoverages ?? [];

    if (
        selection.selectedAdditionalCoverages &&
        (selection?.selectedAdditionalCoverages[0]?.coverageAlias !== packageData.additionalCoverages[0] ||
            selection?.selectedAdditionalCoverages.length !== packageData.additionalCoverages.length)
    ) {
        selectedAdditionalCoverages = packageData.additionalCoverages?.map((alias: string) => {
            return { coverageAlias: alias, selected: false };
        });
    }

    setSelectionValue(
        dispatch,
        selection,
        {
            key: 'selectedAdditionalCoverages',
            value: selectedAdditionalCoverages
        },
        true
    );
};

export const handlePackagesRequest = async ({
    config,
    dispatch,
    logHandlePackagesRequestError,
    selection,
    model,
    user,
    logWarn
}: Required<OnloadFnPackagePage>) => {
    setDidClickFetchPackages({ dispatch, didClick: true });
    const logPackageError = (error: any) => {
        // Mask cprNo in payload for logging
        const payloadMessage = `Creating packages failed: ${JSON.stringify(payload).replace(/("cprNo"\s*:\s*"\d{6})\d{4}(")/gm, '$1****$2')}`;

        if (error.errorCode === 400 && error.message === 'Unable to create valid quotes') {
            logWarn(`Quote unable to create in backend - 0 price ${error.message}`);
        } else {
            logHandlePackagesRequestError(`${payloadMessage} \n\n with error: ${error.message}`);
        }
        track.fault(payloadMessage, errorTypes.tech);
    };
    let payload: any;
    try {
        // get packages
        const packageEndpoint = (user as IUserContextProps).loggedIn
            ? config.endpoint.packagesPrivate
            : config.endpoint.packagesPublic;

        if (config.product === ProductNames.CAR) {
            payload = {
                brand: 'TD',
                productKey: config.product,
                yearlyMillage: selection.mileage ?? 15000,
                alreadyCovered: !!selection.alreadyCovered,
                ownCarYears: selection.ownCarYears,
                numberOfClaims: selection.numberOfClaims,
                latestClaimYear: (selection.numberOfClaims as number) > 0 ? selection.latestClaimYear : undefined,
                leasingCar: !!selection.leasingCar || (!!model.CAR.dmr && !!model.CAR.dmr.isLeasingCar),
                kvhxKey: selection.address ? selection.address.kvhx : undefined,
                cprNo: selection.cprNo ?? undefined,
                leasingCarReturn: model['CAR'].showLeasingCarReturn
            };
            if (selection.registrationNumber) {
                payload.registrationNumber = selection.registrationNumber;
            } else if (selection.viNumber) {
                payload.vin = selection.viNumber;
                payload.partner = selection.partnerInfo?.key ?? '';
            } else {
                payload.variantId = selection.variantId;
                payload.vintage = selection.vintage;
            }

            const packageData = await genericRequest(packageEndpoint, payload).catch((error) => {
                logPackageError(error.response.data);
            });

            dispatch({
                type: SET_DATA_MODEL_VALUE,
                payload: {
                    productKey: config.product,
                    dataKey: 'packages',
                    value: packageData
                }
            });
            setSelectionValue(dispatch, selection, { key: 'packageInfo', value: PackageInfo.COMPREHENSIVE }, true);
        } else if (config.product === ProductNames.CHILD) {
            payload = {
                brand: 'TD',
                productKey: config.product,
                kvhxKey: selection.address ? selection.address.kvhx : null,
                cprNo: selection.cprNo ?? null,
                children: selection.numberOfChildren
            };

            if (selection.partner) {
                payload.partner = selection.partner === WhitelabelPartner.BABY_SAM ? 'BSA' : 'UNKNOWN';
            }

            const packageData = await genericRequest(packageEndpoint, payload).catch((error) => {
                logPackageError(error.response.data);
            });
            packageData.packages = fixDefaultPackageForChild(
                packageData.packages,
                selection.criticalIllnessCoverage as boolean
            );

            dispatch({
                type: SET_DATA_MODEL_VALUE,
                payload: {
                    productKey: config.product,
                    dataKey: 'packages',
                    value: packageData
                }
            });
        }
    } catch (error: any) {
        const errorData = {
            dispatch,
            errorMessage: error.message,
            hasError: true,
            operation: 'Failing when fetch packages',
            redirectToPage: ''
        };
        setError(errorData);
    }
};

export const finalizePurchase = async (
    {
        config,
        dispatch,
        selection,
        model,
        user,
        logFinalizePurchaseError,
        logFinalizePurchaseEmailError
    }: Required<OnloadFnFinalizePurchasePage>,
    pContext: PContext
) => {
    const { selectedPackage, selectedDeductible } = pContext;

    setSelectionValue(dispatch, selection, { key: 'didClickFinalize', value: false }, true);

    try {
        // get stakeholder if not set
        let stakeholder: Stakeholder = {} as Stakeholder;
        if ((user as IUserContextProps).loggedIn && !model[config.product].stakeholder) {
            stakeholder = await getStakeholder({ config, dispatch });
        }
        const stakeholderObject = model[config.product].stakeholder ?? stakeholder;
        const additionalCoverages = selection.selectedAdditionalCoverages
            ? selection.selectedAdditionalCoverages
                  .filter((cov: { selected: boolean }) => cov.selected)
                  .reduce((result: string[], item: { coverageAlias: string }) => {
                      return [...result, getCoverageNameByAlias(item.coverageAlias, true) || item.coverageAlias];
                  }, [])
            : [];
        let buyPayload: any = {
            productKey: config.product,
            brand: 'TD',
            quoteId: selectedPackage.quoteId,
            emailAddress: selection.emailAddress || stakeholderObject.emailAddress,
            productId: selectedPackage.productId,
            premiumRate: +selection.premiumRate!,
            packageName: selectedPackage.packageName,
            eboks: selection.eboksSubscription
            //accept: ,
        };

        if (selection.viNumber && selection.partner) {
            const partnerInfo = selection.partnerInfo;
            buyPayload = {
                ...buyPayload,
                partnerDetails: {
                    vin: partnerInfo?.vin,
                    cvr: partnerInfo?.cvr,
                    dealer: partnerInfo?.dealer,
                    agent: partnerInfo?.agent,
                    partner: partnerInfo?.key,
                    model: partnerInfo?.model
                }
            };
        } else if (config.product === ProductNames.CHILD) {
            buyPayload = {
                ...buyPayload,
                stakeholders: model[ProductNames.CHILD].childList.map((child) => {
                    const firstName = child.fullName.slice(0, child.fullName.lastIndexOf(' '));
                    const lastName = child.fullName.slice(child.fullName.lastIndexOf(' ') + 1);
                    return {
                        cprNo: child.cprNumber,
                        firstName: firstName,
                        lastName: lastName
                    };
                })
            };

            if (selection.partner) {
                buyPayload = {
                    ...buyPayload,
                    partnerDetails: {
                        partner: selection.partner === WhitelabelPartner.BABY_SAM ? 'BSA' : 'UNKNOWN'
                    }
                };
            }
        }

        const { insuranceActivated, stakeholderMissing } = await genericRequest(config.endpoint.buy, buyPayload);

        if (!insuranceActivated) {
            const getEffectiveDate = () => {
                if (selection.effectiveDate === null) {
                    return dateFormatter.ddMMyyyy(new Date(selection.insuranceStartDate as string));
                } else {
                    return selection.isCancelCurrentInsuranceImmediately
                        ? `${selection.effectiveDate} - ${dateFormatter.ddMMyyyy(
                              new Date(selection.insuranceStartDate as string)
                          )}`
                        : selection.effectiveDate;
                }
            };
            // sales complete email
            let emailPayload: IEmailPayload = {
                event: BLOCK_TYPES.SALES_COMPLETE,
                identifier: `${config.product}_SALES_COMPLETE` as EMAIL_IDENTIFIER,
                quoteId: selectedPackage.quoteId,
                cprNumber: stakeholderObject.cprNo,
                fullName: `${stakeholderObject.firstName} ${stakeholderObject.lastName}`,
                residentialAddress:
                    selection.address?.address ?? `${stakeholderObject.streetAddress}, ${stakeholderObject.zipCity}`,
                phoneNumber: selection.mobilePhone ?? stakeholderObject.mobilePhone ?? '',
                emailAddress: (selection.emailAddress ?? stakeholderObject.emailAddress) as string,
                effectiveDate: `${getEffectiveDate()}`,
                currentInsuranceCompany: selection.currentInsuranceCompany as string,
                bankRegistrationNumber: selection.bankRegNumber,
                bankAccountNumber: selection.bankAccountNumber,
                premiumRateDescription: parseInt(selection.premiumRate ?? '1') === 1 ? 'Årligt' : 'Månedligt',
                packageName: selectedPackage.packageName,
                deductible: selectedDeductible.toString(),
                additionalCoverages: additionalCoverages,
                eboks: selection.eboksSubscription as boolean
            };

            if (config.product === ProductNames.CAR) {
                //TODO: Enabled and set to deductibleBasis when BE is fixed
                //const deductible = model[ProductNames.CAR].packages?.deductibles.find(
                //    (p: Deductible) => p.deductibleBasis === selectedDeductible
                //);
                //emailPayload.deductible = deductible?.amount.toString() ?? '';*/
                emailPayload.deductible = selectedDeductible.toString() ?? '';
                emailPayload.registrationNumber = selection.registrationNumber;
                emailPayload.manufacturer = model.CAR.dmr?.manufacturerName;
                emailPayload.model = model.CAR.dmr?.modelName;
                emailPayload.motorSize = selection.motorSize;
                emailPayload.variant = model.CAR.dmr?.variantTypeName;
                emailPayload.enginePower = selection.power ? `${selection.power} Watt` : '';
                emailPayload.vintage = model.CAR.dmr?.originalRegistrationDate;
                emailPayload.leasingCar = !!selection.leasingCar;
                emailPayload.ownCarInsurance = selection.ownCarYears;
                emailPayload.yearlyMillage = selection.mileage?.toString() ?? '15000';
                emailPayload.numberOfClaims = selection?.numberOfClaims;
                emailPayload.latestClaimYear = selection.latestClaimYear;
                emailPayload.packageInfo = selection.packageInfo === PackageInfo.LIABILITY_ONLY ? 'ANSVAR' : 'KASKO'; //liability-only: 'ANSVAR' otherwise 'KASKO
                if (selection.viNumber) {
                    emailPayload.vin = selection?.viNumber;
                    emailPayload.partner = selection.partner;
                }
                emailPayload.price = selectedPackage.monthlyPrice;
            } else if (config.product === ProductNames.CHILD) {
                emailPayload.children = selection.numberOfChildren;
                emailPayload.stakeholderMissing = !!stakeholderMissing;
                emailPayload.childrenInformation = model[ProductNames.CHILD].childList.map((child) => ({
                    fullName: child.fullName,
                    cprNumber: child.cprNumber,
                    age: child.age
                }));

                if (selection.partner) {
                    emailPayload.partner = selection.partner === WhitelabelPartner.BABY_SAM ? 'BSA' : 'UNKNOWN';
                }
            }

            try {
                await genericRequest(config.endpoint.email, emailPayload);
                track.info([{ key: 'business-email-sent', value: config.product }]);
                dispatch({
                    type: SET_PURCHASE_COMPLETE,
                    payload: true
                });
            } catch (error: any) {
                logFinalizePurchaseEmailError(error.message);
                await track.fault(`email endpoint failed: ${JSON.stringify(error)}`, errorTypes.tech);
            }
        }

        await track.convert([{ key: 'purchase-complete-buyendpoint', value: config.product }]);
    } catch (error: any) {
        dispatch({
            type: SET_PURCHASE_COMPLETE,
            payload: false
        });

        logFinalizePurchaseError(error.message);
        await track.fault(`buy endpoint failed: ${JSON.stringify(error)}`, errorTypes.tech);

        throw new Error(JSON.stringify(error));
    }
};

export const sendRmoEMail = async ({ config, selection, model, error, flow, eventKey, logRMOError }: SendRmoEMail) => {
    const isGeneralError = flow.blockType === BLOCK_TYPES.GENERAL_ERROR;
    const getIdentifier = () => {
        if (flow.blockType === BLOCK_TYPES.GENERAL_ERROR || eventKey === BLOCK_TYPES.WILL_NOT_INFORM_CPR) {
            return EMAIL_IDENTIFIER.GENERAL_CALLME;
        } else if (
            flow.blockType === BLOCK_TYPES.CAR_NO_CONTENT_37_38 ||
            flow.blockType === BLOCK_TYPES.CAR_DATA_DEFICIENT
        ) {
            return EMAIL_IDENTIFIER.CAR_CALLME;
        }
        return EMAIL_IDENTIFIER.CHILD_CALLME;
    };

    try {
        let emailPayload = {} as RMOEmailPayloadCar | RMOEmailPayloadChild;

        const stakeholderObject = model[config.product].stakeholder;
        // set package. If user did not actively select a package, set package from default
        const p = model[config.product].packages?.packages.find((d: Package) => d.defaultSelected);
        const selectedPackage = selection.package ?? p;

        // set deductible. If user did not actively select it, set from default
        const deductible =
            selection.deductible ??
            model[config.product].packages?.deductibles?.find((d: Deductible) => d.defaultDeductible)?.amount;

        const additionalCoverages = selection.selectedAdditionalCoverages
            ? selection.selectedAdditionalCoverages
                  .filter((cov: { selected: boolean }) => cov.selected)
                  .reduce((result: string[], item: { coverageAlias: string }) => {
                      return [...result, getCoverageNameByAlias(item.coverageAlias, true) || item.coverageAlias];
                  }, [])
            : [];
        let address = '';
        if (selection.address?.address || stakeholderObject?.streetAddress) {
            address =
                (selection.address?.address ??
                    `${stakeholderObject?.streetAddress ?? ''}, ${stakeholderObject?.zipCity ?? ''}`) ||
                selection.address?.address ||
                '';
        }

        let commonEmailPayload = {
            identifier: getIdentifier(),
            event: (eventKey ?? flow.blockType) as BLOCK_TYPES,
            fullName: stakeholderObject?.firstName
                ? `${stakeholderObject?.firstName} ${stakeholderObject?.lastName}`
                : selection.fullName,
            phoneNumber: stakeholderObject?.mobilePhone ?? selection.phoneNumber ?? '',
            productName: productName(config.product)
        } as RMOEmailPayloadCommon;

        if (flow.blockType === BLOCK_TYPES.GENERAL_ERROR) {
            commonEmailPayload.operation = error?.operation ?? 'No operation specified';
        }

        if (
            //flow.blockType === BLOCK_TYPES.GENERAL_ERROR ||
            eventKey === BLOCK_TYPES.WILL_NOT_INFORM_CPR ||
            flow.blockType === BLOCK_TYPES.CUSTOMER_BELOW_18
        ) {
            emailPayload = {
                ...commonEmailPayload,
                identifier: EMAIL_IDENTIFIER.GENERAL_CALLME
            };
        } else if (flow.blockType === BLOCK_TYPES.UNSUPPORTED_ADDRESS_BBR) {
            emailPayload = {
                ...commonEmailPayload,
                address: address,
                identifier: EMAIL_IDENTIFIER.GENERAL_CALLME
            };
        } else if (config.product === ProductNames.CHILD) {
            emailPayload = {
                ...commonEmailPayload,
                identifier: isGeneralError ? EMAIL_IDENTIFIER.GENERAL_CALLME : EMAIL_IDENTIFIER.CHILD_CALLME,
                address: address,
                birthDate: cpr2BirthDate(stakeholderObject?.cprNo ?? selection?.cprNo ?? ''),
                criticalIllnessCoverage: selection.criticalIllnessCoverage as boolean,
                packageName: selectedPackage?.packageName,
                disabilitySum: selection.disabilitySum,
                premiumRateDescription: 'Månedligt',
                effectiveDate: (selection.effectiveDate ?? selection.insuranceStartDate) as string,
                price: selection.package?.monthlyPrice
                    ? new Intl.NumberFormat('da-DK').format(selection.package.monthlyPrice) + ' kr.'
                    : '',
                partner: selection.partner === WhitelabelPartner.BABY_SAM ? 'BSA' : 'Ukendt'
            } as RMOEmailPayloadChild;
        } else if (config.product === ProductNames.CAR) {
            const altManufactor =
                model.CAR.manufactors.find((m) => m.value.toLowerCase() === selection.manufactor?.toLowerCase())
                    ?.label ?? '';
            const altFuel =
                model.CAR.fuels.find((m) => m.value.toLowerCase() === selection.fuel?.toLowerCase())?.label ?? '';
            const altModel =
                model.CAR.models.find((m) => m.value.toLowerCase() === selection.model?.toLowerCase())?.label ?? '';
            const altYear =
                model.CAR.years.find((m) => m.value.toLowerCase() === selection.vintage?.toLowerCase())?.label ?? '';
            const altEngine =
                model.CAR.engines.find((m) => m.value.toLowerCase() === selection.motorSize?.toLowerCase())?.label ??
                '';
            const altPower =
                model.CAR.powers.find((m) => m.value.toLowerCase() === selection.power?.toLowerCase())?.label +
                    ' Watt' || '';
            emailPayload = {
                ...commonEmailPayload,
                identifier: isGeneralError ? EMAIL_IDENTIFIER.GENERAL_CALLME : EMAIL_IDENTIFIER.CAR_CALLME,
                registrationNumber: selection.registrationNumber,
                vin: selection.viNumber ?? 'Ikke benyttet',
                manufacturer: model.CAR.dmr?.manufacturerName ?? altManufactor,
                model: model.CAR.dmr?.modelName ?? altModel,
                fuelType: altFuel,
                variant: (model.CAR.dmr?.variantTypeName ?? isGeneralError) ? '' : 'Min bil er ikke på listen',
                motorSize: altEngine,
                enginePower: altPower.includes('undefined') ? '' : altPower,
                vintage: model.CAR.dmr?.originalRegistrationDate ?? altYear,
                leasingCar: !!selection.leasingCar || (!!model.CAR.dmr && !!model.CAR.dmr.isLeasingCar),
                address: address,
                birthDate: cpr2BirthDate(stakeholderObject?.cprNo ?? selection?.cprNo ?? ''),
                ownCarInsurance: selection.ownCarYears,
                yearlyMillage: (selection.mileage?.toString() ?? isGeneralError) ? '' : `15000`,
                packageName: selectedPackage?.packageName ?? undefined,
                deductible: deductible?.toString(),
                additionalCoverages: additionalCoverages,
                numberOfClaims: selection.numberOfClaims,
                effectiveDate: (selection.effectiveDate ?? selection.insuranceStartDate) as string,
                premiumRateDescription: 'Månedligt',
                price: selection.package?.monthlyPrice
                    ? new Intl.NumberFormat('da-DK').format(selection.package.monthlyPrice) + ' kr.'
                    : '',
                partner: selection.partner === WhitelabelPartner.DANSK_BILBRANCHERÅD ? 'DBR' : 'Ukendt'
            } as RMOEmailPayloadCar;
        }

        await genericRequest(config.endpoint.email, emailPayload);
    } catch (error: any) {
        logRMOError(error.message);
        throw new Error(JSON.stringify(error));
    }
};

// Call BBR to check an address. We need an ID that is in the string passed in.
// Return undefined if the ID could not be found/used.
export const callBBR = async (kvhxURL: string) => {
    const BBRURL = 'https://services.datafordeler.dk/DAR/DAR/1/rest/adresseTilHusnummer?format=JSON&adresseId=';
    const getIdRegex = /^.*\/(.*)$/;
    const result = getIdRegex.exec(kvhxURL);
    if (result === null) {
        return undefined;
    }
    if (result.length === 2) {
        const id = result[1];
        const bbr: string = await genericRequest({ url: BBRURL + id, method: 'GET', headers: {} });
        return bbr?.length > 0;
    } else {
        return undefined;
    }
};

/**
 * Terminate insurance a.s.a.p.
 * Next 1st plus two or three month
 */
export const asap = () => {
    const date = new Date();
    const monthDiff = date.getDate() > 24 ? 3 : 2;
    date.setMonth(date.getMonth() + monthDiff);
    date.setDate(1);

    return date;
};

export const getCoverageNameByAlias = (coverageAlias: string, isPayload = false): string => {
    switch (coverageAlias.toLocaleUpperCase()) {
        case 'HEALTH_INSURANCE':
            return 'Sundhedsforsikring';
        case 'ROAD_ASSISTANCE':
            return 'Vejhjælp';
        case 'EXTENDED_ROAD_ASSISTANCE':
            return 'Udvidet vejhjælp';
        case 'YOUNG_DRIVERS':
            return !isPayload ? 'Ung bilist' : 'Unge bilister';
        case 'LEASING_CAR_RETURN':
            return 'Leasing retur';
        default:
            return '';
    }
};

export const vincheck = async ({ endpoint, vinToCheck }: VinCheck) => {
    if (!vinToCheck || !endpoint) {
        return false;
    }
    const method = endpoint.method;
    const headers = endpoint.headers;
    const url = endpoint.url;

    return await genericRequest({ method, url, headers }, { vin: vinToCheck });
};

export const getUrlParams = (search: string) => {
    const params = new URLSearchParams(search);

    return {
        vin: params.get('v'),
        source: params.get('s'),
        partner: params.get('p')
    };
};

/*
  This call is made because BE has to do a risc calculation, when customer enters an unknown address.
  If we wait until the package page to call for packages customers will see an error message - 0 prices.
  The response will be ignored.
*/
export const riscCheckCall = (endpoint: Endpoint, kvhxKey: string) => {
    const body = {
        brand: 'TD',
        productKey: 'CONTENT',
        kvhxKey: kvhxKey,
        householdSize: 1,
        alreadyCovered: false,
        numberOfClaims: 0
    };
    /*
    const options = getDefaultOptions(HTTP_METHOD.POST, props, 'wso2', body);
    const loggedOn = window.topContext.user.isLoggedOn;
    const baseUrl = loggedOn ? props.config.wso2ApiUrl : props.config.wso2OpenApiUrl;
    fetch(`${baseUrl}/packages/initRiskCalculation`, options);
    */
    const method = endpoint.method;
    const url = endpoint.url;
    const headers = endpoint.headers;
    genericRequest({ method, url, headers }, body);
};
